import tippy from "tippy.js";

function exitActiveSuggestion(view) {
  if (!view?.state) {
    return;
  }

  try {
    const { exitSuggestion } = require("@tiptap/suggestion");
    exitSuggestion(view);
    return;
  } catch {
    // Fall back when @tiptap/suggestion is unavailable in the test/runtime bundle.
  }

  const suggestionPlugin = view.state.plugins.find(
    (plugin) => plugin.spec?.key?.key === "suggestion"
  );
  const pluginKey = suggestionPlugin?.spec?.key;
  if (pluginKey) {
    view.dispatch(view.state.tr.setMeta(pluginKey, { exit: true }));
  }
}

const SLASH_LINE_PATTERN = /^\/\w*$/;

const dismissedBlocks = new Set();
const dismissedSlashQueries = new Map();

function getSlashQuery(blockText) {
  if (!SLASH_LINE_PATTERN.test(blockText)) {
    return null;
  }

  return blockText.slice(1);
}

function getSlashDismissQuery(blockText) {
  if (!blockText.startsWith("/")) {
    return null;
  }

  const slashQuery = getSlashQuery(blockText);
  if (slashQuery !== null) {
    return slashQuery;
  }

  // Broken slash lines such as "/bold " still carry a filter prefix.
  return blockText.slice(1).match(/^\w*/)?.[0] ?? "";
}

export function syncSlashDismissState(state, oldState) {
  if (!state?.selection?.$from) {
    return;
  }

  const $from = state.selection.$from;
  const blockText = $from.parent.textContent;
  const blockPos = $from.start();
  const slashQuery = getSlashQuery(blockText);

  if (slashQuery === null) {
    if (!blockText.startsWith("/")) {
      clearBlockDismissed(blockPos);
    }
    return;
  }

  if (oldState?.selection?.$from) {
    const oldText = oldState.selection.$from.parent.textContent;
    if (blockText.length < oldText.length) {
      const oldDismissQuery = getSlashDismissQuery(oldText);
      if (oldDismissQuery !== null) {
        markBlockDismissed(blockPos, oldDismissQuery);
      } else if (slashQuery === "") {
        dismissedSlashQueries.set(blockPos, "");
      } else if (dismissedBlocks.has(blockPos)) {
        dismissedSlashQueries.set(blockPos, slashQuery);
      }
    } else if (blockText.length > oldText.length) {
      clearBlockDismissed(blockPos);
      return;
    }
  }

  if (!dismissedBlocks.has(blockPos)) {
    return;
  }

  if (slashQuery === "") {
    return;
  }
}

export function shouldResetSlashDismissed({ state }) {
  if (!state?.selection?.$from) {
    return true;
  }

  const $from = state.selection.$from;
  const blockText = $from.parent.textContent;
  const blockPos = $from.start();
  const slashQuery = getSlashQuery(blockText);

  if (slashQuery === null) {
    return !blockText.startsWith("/");
  }

  if (slashQuery === "") {
    return !dismissedBlocks.has(blockPos);
  }

  if (!dismissedBlocks.has(blockPos)) {
    return true;
  }

  const dismissedQuery = dismissedSlashQueries.get(blockPos);
  if (dismissedQuery === undefined) {
    return false;
  }

  if (slashQuery.length > dismissedQuery.length) {
    return true;
  }

  if (!dismissedQuery.startsWith(slashQuery)) {
    return true;
  }

  return false;
}

export function isEmptyLineSlashAllowed({ state, range }) {
  const $from = state.doc.resolve(range.from);
  const blockText = $from.parent.textContent;
  const blockPos = $from.start();
  const slashQuery = getSlashQuery(blockText);

  syncSlashDismissState(state);

  if (slashQuery === null) {
    return false;
  }

  if (!dismissedBlocks.has(blockPos)) {
    return true;
  }

  if (slashQuery === "") {
    return !dismissedBlocks.has(blockPos);
  }

  const dismissedQuery = dismissedSlashQueries.get(blockPos);
  if (dismissedQuery === undefined) {
    return false;
  }

  if (slashQuery.length > dismissedQuery.length) {
    clearBlockDismissed(blockPos);
    return true;
  }

  if (
    dismissedQuery.startsWith(slashQuery) &&
    slashQuery.length <= dismissedQuery.length
  ) {
    return false;
  }

  clearBlockDismissed(blockPos);
  return true;
}

export function dismissSlashMenuForBackspace(state) {
  if (!state?.selection?.$from) {
    return;
  }

  const $from = state.selection.$from;
  const blockText = $from.parent.textContent;
  const blockPos = $from.start();
  const dismissQuery = getSlashDismissQuery(blockText);

  if (dismissQuery === null) {
    return;
  }

  markBlockDismissed(blockPos, dismissQuery);
}

export function isSlashMenuVisible(props) {
  const state = props?.editor?.state;
  const range = props?.range;
  if (!state || !range) {
    return true;
  }

  return isEmptyLineSlashAllowed({ state, range });
}

export function markBlockDismissed(blockPos, slashQueryAfterDismiss) {
  dismissedBlocks.add(blockPos);
  if (typeof slashQueryAfterDismiss === "string") {
    dismissedSlashQueries.set(blockPos, slashQueryAfterDismiss);
  }
}

export function clearBlockDismissed(blockPos) {
  dismissedBlocks.delete(blockPos);
  dismissedSlashQueries.delete(blockPos);
}

export function clearAllDismissed() {
  dismissedBlocks.clear();
  dismissedSlashQueries.clear();
}

export function filterSlashItems(items, query) {
  const normalizedQuery = query.toLowerCase();
  if (!normalizedQuery) {
    return items;
  }
  return items.filter((item) =>
    item.title.toLowerCase().startsWith(normalizedQuery)
  );
}

export function createSuggestionItems({
  onLinkRequest,
  onImageRequest,
  onYoutubeRequest,
  onDateRequest,
  onEquationRequest,
  onMathFormulaRequest,
} = {}) {
  return ({ query }) => {
    const items = [
      {
        title: "Heading 1",
        icon: "H1",
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode("heading", { level: 1 })
            .run();
        },
      },
      {
        title: "Heading 2",
        icon: "H2",
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode("heading", { level: 2 })
            .run();
        },
      },
      {
        title: "Heading 3",
        icon: "H3",
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode("heading", { level: 3 })
            .run();
        },
      },
      {
        title: "Bullet List",
        icon: "•",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBulletList().run();
        },
      },
      {
        title: "Numbered List",
        icon: "1.",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleOrderedList().run();
        },
      },
      {
        title: "Task List",
        icon: "☑",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleTaskList().run();
        },
      },
      {
        title: "Quote",
        icon: '"',
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBlockquote().run();
        },
      },
      {
        title: "Divider",
        icon: "—",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setHorizontalRule().run();
        },
      },
      {
        title: "Table",
        icon: "⊞",
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run();
        },
      },
      {
        title: "Link",
        icon: "🔗",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.setTimeout(() => onLinkRequest?.(), 0);
        },
      },
      {
        title: "Image",
        icon: "🖼",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          onImageRequest?.();
        },
      },
      {
        title: "YouTube",
        icon: "▶",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          onYoutubeRequest?.();
        },
      },
      {
        title: "Date",
        icon: "📅",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.setTimeout(() => onDateRequest?.(), 0);
        },
      },
      {
        title: "Equation",
        icon: "∑",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.setTimeout(() => onEquationRequest?.(), 0);
        },
      },
      {
        title: "Math Formula",
        icon: "√",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.setTimeout(() => onMathFormulaRequest?.(), 0);
        },
      },
      {
        title: "Underline",
        icon: "U",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleUnderline().run();
        },
      },
      {
        title: "Code Block",
        icon: "<>",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
        },
      },
      {
        title: "Code",
        icon: "`",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleCode().run();
        },
      },
      {
        title: "Highlight",
        icon: "⚠",
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleHighlight().run();
        },
      },
    ];

    return filterSlashItems(items, query);
  };
}

export function getDisplayOrderedItems(items) {
  return items;
}

function buildMenuHtml(displayItems, selectedIndex) {
  const itemsHTML = displayItems
    .map((item, index) => {
      const stubClass = item.stub ? " slash-menu-item--stub" : "";
      return `
              <button class="slash-menu-item${
                index === selectedIndex ? " is-selected" : ""
              }${stubClass}" data-index="${index}" data-item-title="${item.title}" type="button">
                <div class="slash-menu-icon-wrapper">
                  <span class="default-icon">${item.icon || item.title.charAt(0)}</span>
                </div>
                <div class="slash-menu-text">
                  <div class="slash-menu-label">${item.title}</div>
                </div>
              </button>
            `;
    })
    .join("");

  return `
        <div class="slash-menu-container">
          <div class="slash-menu-list">
            ${itemsHTML}
          </div>
        </div>
      `;
}

function updateSlashDecoration(query) {
  const node = document.querySelector(".slash-command-suggestion");
  if (!node) {
    return;
  }
  if (query.length === 0) {
    node.setAttribute("data-empty", "true");
  } else {
    node.removeAttribute("data-empty");
  }
}

function clearSlashDecoration() {
  const node = document.querySelector(".slash-command-suggestion");
  if (node) {
    node.removeAttribute("data-empty");
  }
}

function removeOrphanSlashMenus() {
  if (typeof document === "undefined") {
    return;
  }

  const destroyTippyRoot = (root) => {
    if (!root) {
      return;
    }
    root._tippy?.destroy?.();
    root.remove();
  };

  document.querySelectorAll(".slash-commands-menu").forEach((menu) => {
    destroyTippyRoot(menu.closest("[data-tippy-root]"));
  });

  document.querySelectorAll("[data-tippy-root]").forEach((root) => {
    if (
      root.querySelector(".slash-menu-container, .slash-commands-menu") ||
      root.querySelector('.tippy-box[data-theme="slash-menu"]')
    ) {
      destroyTippyRoot(root);
    }
  });
}

export function dismissSlashMenuUI(view) {
  if (view) {
    exitActiveSuggestion(view);
  }

  removeOrphanSlashMenus();
  clearSlashDecoration();
}

export function destroyAllSlashMenus(view) {
  dismissSlashMenuUI(view);
  clearAllDismissed();
}

function bindMenuInteraction(menuRoot, getContext) {
  if (menuRoot.dataset.bound === "true") {
    return;
  }

  menuRoot.dataset.bound = "true";

  menuRoot.addEventListener("mousedown", (event) => {
    const button = event.target.closest(".slash-menu-item");
    if (!button || !menuRoot.contains(button)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const title = button.dataset.itemTitle;
    const { displayItems, command } = getContext();
    const item = displayItems.find((entry) => entry.title === title);
    if (item && command) {
      command(item);
    }
  });

  menuRoot.addEventListener("mouseover", (event) => {
    const button = event.target.closest(".slash-menu-item");
    if (!button || !menuRoot.contains(button)) {
      return;
    }

    const index = Number(button.dataset.index);
    if (!Number.isNaN(index)) {
      getContext().updateSelection(index);
    }
  });
}

export function renderSlashMenuItems() {
  let component;
  let popup;
  let selectedIndex = 0;
  let menuProps = null;
  let displayItems = [];
  let commandExecuted = false;
  let blockPos = null;
  let lastQuery = null;
  let catalogItems = [];

  const mergeCatalogItems = (items) => {
    if (!items?.length) {
      return;
    }

    const itemsByTitle = new Map(
      catalogItems.map((item) => [item.title, item])
    );
    items.forEach((item) => {
      itemsByTitle.set(item.title, item);
    });
    catalogItems = Array.from(itemsByTitle.values());
  };

  const executeCommand = (item) => {
    commandExecuted = true;
    if (menuProps?.command) {
      menuProps.command(item);
    }
  };

  const getContext = () => ({
    displayItems,
    command: executeCommand,
    updateSelection,
  });

  const updateSelection = (newIndex) => {
    if (!component) {
      return;
    }

    const buttons = component.querySelectorAll(".slash-menu-item");
    buttons.forEach((button) => {
      const index = Number(button.dataset.index);
      if (index === newIndex) {
        button.classList.add("is-selected");
        if (typeof button.scrollIntoView === "function") {
          button.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      } else {
        button.classList.remove("is-selected");
      }
    });
    selectedIndex = newIndex;
  };

  const syncMenuContent = () => {
    if (!component) {
      return;
    }

    component.innerHTML = buildMenuHtml(displayItems, selectedIndex);
  };

  const resolveDisplayItems = (items, query) => {
    if (!items?.length) {
      return [];
    }

    return getDisplayOrderedItems(filterSlashItems(items, query || ""));
  };

  const resolveSlashQuery = (props) => {
    if (typeof props.text === "string" && props.text.startsWith("/")) {
      return props.text.slice(1);
    }

    return props.query || "";
  };

  const destroySlashMenu = () => {
    popup?.destroy();
    popup = null;
    component?.remove();
    component = null;
    removeOrphanSlashMenus();
  };

  const ensurePopup = (props) => {
    if (popup || !component) {
      return;
    }

    const clientRect = props.clientRect || menuProps?.clientRect;
    if (typeof clientRect !== "function") {
      return;
    }

    popup = tippy(document.body, {
      getReferenceClientRect: clientRect,
      appendTo: () => document.body,
      content: component,
      showOnCreate: true,
      interactive: true,
      trigger: "manual",
      placement: "bottom-start",
      theme: "slash-menu",
      arrow: false,
      offset: [0, 8],
      maxWidth: "none",
      popperOptions: {
        strategy: "fixed",
      },
      onMount(instance) {
        instance.popper.style.zIndex = "50";
      },
    })[0];
  };

  const refreshSlashMenu = (props) => {
    menuProps = props;
    mergeCatalogItems(props.items);
    const nextQuery = resolveSlashQuery(props);
    displayItems = resolveDisplayItems(catalogItems, nextQuery);

    if (lastQuery !== nextQuery) {
      selectedIndex = 0;
      lastQuery = nextQuery;
    }

    if (selectedIndex >= displayItems.length) {
      selectedIndex = 0;
    }

    syncMenuContent();
    updateSelection(selectedIndex);

    ensurePopup(props);

    if (!popup) {
      return;
    }

    if (!displayItems.length) {
      popup.hide();
      return;
    }

    popup.show();
    popup.setProps({
      getReferenceClientRect: props.clientRect || menuProps.clientRect,
    });
  };

  const ensureSlashMenuComponent = (props) => {
    if (component) {
      return true;
    }

    if (!props.items?.length && !catalogItems.length) {
      return false;
    }

    component = document.createElement("div");
    component.className = "slash-commands-menu";
    bindMenuInteraction(component, getContext);

    const state = props.editor?.state;
    if (state && props.range) {
      const $from = state.doc.resolve(props.range.from);
      blockPos = $from.start();
    }

    return true;
  };

  return {
    onStart(props) {
      if (!isSlashMenuVisible(props)) {
        destroySlashMenu();
        return false;
      }

      updateSlashDecoration(resolveSlashQuery(props));
      destroySlashMenu();

      commandExecuted = false;
      lastQuery = null;
      catalogItems = [];
      mergeCatalogItems(props.items);
      if (!props.items?.length && !catalogItems.length) {
        return false;
      }

      const state = props.editor?.state;
      if (state && props.range) {
        const $from = state.doc.resolve(props.range.from);
        blockPos = $from.start();
      }

      if (!ensureSlashMenuComponent(props)) {
        return false;
      }

      menuProps = props;
      ensurePopup(props);
      refreshSlashMenu(props);

      return displayItems.length > 0;
    },

    onUpdate(props) {
      if (!isSlashMenuVisible(props)) {
        destroySlashMenu();
        return;
      }

      updateSlashDecoration(resolveSlashQuery(props));
      mergeCatalogItems(props.items);
      if (!ensureSlashMenuComponent(props)) {
        return;
      }

      refreshSlashMenu(props);
    },

    onKeyDown(props) {
      if (props.event.key === "Backspace") {
        const activeBlockPos = blockPos;
        destroySlashMenu();

        if (props.view?.state) {
          dismissSlashMenuForBackspace(props.view.state);
        } else if (activeBlockPos !== null) {
          markBlockDismissed(activeBlockPos);
        }

        if (props.view) {
          exitActiveSuggestion(props.view);
        }

        window.setTimeout(() => {
          if (!props.view?.state || activeBlockPos === null) {
            return;
          }

          syncSlashDismissState(props.view.state);
        }, 0);

        return false;
      }

      // TipTap 3.x only passes { view, event, range } — use cached menu state.
      if (!displayItems.length) {
        return false;
      }

      if (props.event.key === "ArrowUp") {
        const newIndex =
          (selectedIndex - 1 + displayItems.length) % displayItems.length;
        updateSelection(newIndex);
        return true;
      }

      if (props.event.key === "ArrowDown") {
        const newIndex = (selectedIndex + 1) % displayItems.length;
        updateSelection(newIndex);
        return true;
      }

      if (props.event.key === "Enter") {
        const selectedItem = displayItems[selectedIndex];
        if (selectedItem) {
          executeCommand(selectedItem);
        }
        return true;
      }

      if (props.event.key === "Escape") {
        commandExecuted = true;
        destroySlashMenu();
        return true;
      }

      return false;
    },

    onExit() {
      clearSlashDecoration();
      destroySlashMenu();
      menuProps = null;
      displayItems = [];
      selectedIndex = 0;
      lastQuery = null;
      catalogItems = [];
      commandExecuted = false;
      blockPos = null;
    },
  };
}
